import { useQuery } from '@tanstack/react-query';
// `Gift` is only used by the hidden "Redeem a code" card below — restore it
// alongside that section when License Codes ship (Phase 1.1).
import { Calendar, CreditCard, Receipt as ReceiptIcon } from 'lucide-react';
import { api } from '../../../api/client';
import { formatDateTime, formatRelative } from '../../../lib/format';

const ENTITLEMENT_LABEL = {
  starter: 'Starter',
  booster: 'Booster Pack',
  pro: 'Pro',
  autoPilot: 'AutoPilot',
  agency: 'Agency',
  // The `enterprise` entitlement is sold/branded as the Reseller offer.
  enterprise: 'Reseller',
};

// Friendly labels for the legacy tier string surfaced by /billing-summary
// (free | starter | pro | enterprise). Enterprise shows as "Reseller".
const TIER_LABEL = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  enterprise: 'Reseller',
};

const ENTITLEMENT_COLOR = {
  starter: 'bg-emerald-50 text-emerald-700',
  booster: 'bg-amber-50 text-amber-700',
  pro: 'bg-brand-50 text-brand-700',
  autoPilot: 'bg-cyan-50 text-cyan-700',
  agency: 'bg-violet-50 text-violet-700',
  enterprise: 'bg-rose-50 text-rose-700',
};

const EVENT_LABEL = {
  'purchase.initial': { label: 'Sale', color: 'bg-emerald-50 text-emerald-700' },
  'purchase.rebill': { label: 'Rebill', color: 'bg-cyan-50 text-cyan-700' },
  'purchase.refund': { label: 'Refund', color: 'bg-amber-50 text-amber-700' },
  'purchase.chargeback': { label: 'Chargeback', color: 'bg-rose-50 text-rose-700' },
  'subscription.cancel': { label: 'Cancel', color: 'bg-gray-100 text-gray-700' },
  'subscription.expired': { label: 'Expired', color: 'bg-gray-100 text-gray-700' },
  unknown: { label: 'Unknown', color: 'bg-gray-100 text-gray-500' },
};

function formatCents(cents, currency = 'USD') {
  const n = (cents || 0) / 100;
  if (currency === 'USD') return `$${n.toFixed(2)}`;
  return `${n.toFixed(2)} ${currency}`;
}

export default function BillingSection() {
  const summaryQuery = useQuery({
    queryKey: ['billing-summary'],
    queryFn: () => api.get('/api/me/billing-summary').then((r) => r.data),
  });

  const eventsQuery = useQuery({
    queryKey: ['billing-events'],
    queryFn: () => api.get('/api/me/billing-events?limit=10').then((r) => r.data.events),
  });

  const summary = summaryQuery.data;
  const events = eventsQuery.data || [];
  const owned = summary?.entitlements || [];

  return (
    <div className="space-y-4">
      {/* Hero — current tier + entitlement chips */}
      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <CreditCard className="w-4 h-4" />
            </span>
            Current plan
          </div>
          {summary?.tier && (
            <span className="pill bg-brand-50 text-brand-700 capitalize">
              {TIER_LABEL[summary.tier] || summary.tier}
            </span>
          )}
        </div>

        {owned.length === 0 ? (
          <p className="text-sm text-ink-500">
            You&apos;re on the free plan. Browse{' '}
            <a
              href="https://rankonai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-700 underline"
            >
              the pricing page
            </a>{' '}
            to upgrade.
          </p>
        ) : (
          <>
            <p className="text-sm text-ink-500 mb-3">
              Your account currently includes:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {owned.map((key) => (
                <span
                  key={key}
                  className={`pill ${ENTITLEMENT_COLOR[key] || 'bg-gray-100 text-gray-700'}`}
                >
                  {ENTITLEMENT_LABEL[key] || key}
                </span>
              ))}
            </div>
          </>
        )}

        {/* AutoPilot status block */}
        {summary?.autoPilot?.active && (
          <div className="mt-4 rounded-xl border border-cyan-100 bg-cyan-50/40 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <div className="font-medium text-ink-800 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-cyan-600" />
                AutoPilot subscription
              </div>
              <span
                className={`pill ${
                  summary.autoPilot.status === 'active'
                    ? 'bg-emerald-50 text-emerald-700'
                    : summary.autoPilot.status === 'cancelled'
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-gray-100 text-gray-600'
                }`}
              >
                {summary.autoPilot.status || 'active'}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <BillingStat
                label="Interval"
                value={summary.autoPilot.interval || '—'}
                className="capitalize"
              />
              <BillingStat
                label="Started"
                value={
                  summary.autoPilot.startedAt
                    ? formatRelative(summary.autoPilot.startedAt)
                    : '—'
                }
              />
              <BillingStat
                label={summary.autoPilot.cancelledAt ? 'Access until' : 'Next rebill'}
                value={
                  summary.autoPilot.expiresAt
                    ? formatDateTime(summary.autoPilot.expiresAt)
                    : '—'
                }
              />
              <BillingStat
                label="Cancelled"
                value={
                  summary.autoPilot.cancelledAt
                    ? formatRelative(summary.autoPilot.cancelledAt)
                    : 'No'
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Redeem code — hidden until Phase 1.1 License Codes ship. Keep the
          markup (and re-add the `Gift` import) to restore the section.
      <div className="card opacity-80">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Gift className="w-4 h-4" />
            </span>
            Redeem a code
          </div>
          <span className="pill bg-gray-100 text-gray-500">Coming soon</span>
        </div>
        <p className="text-sm text-ink-500 mb-3">
          Got a partner gift code or comp code from support? Paste it here once License
          Codes ship.
        </p>
        <div className="flex gap-2">
          <input
            className="input flex-1 font-mono text-sm"
            placeholder="ABCD-1234-EFGH-5678"
            disabled
          />
          <button className="btn-primary" disabled>
            Redeem
          </button>
        </div>
      </div>
      */}

      {/* Recent purchases */}
      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <ReceiptIcon className="w-4 h-4" />
            </span>
            Recent purchases
            {events.length > 0 && (
              <span className="text-xs text-ink-400 font-normal ml-1">({events.length})</span>
            )}
          </div>
        </div>

        {eventsQuery.isLoading ? (
          <p className="text-sm text-ink-400">Loading…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-ink-400">
            No purchases on file yet. Any Launchpad purchases linked to {' '}
            <strong>your email</strong> will appear here.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 text-sm">
            {events.map((ev) => {
              const info = EVENT_LABEL[ev.eventType] || EVENT_LABEL.unknown;
              return (
                <li
                  key={ev._id}
                  className="py-3 flex items-center justify-between gap-3 flex-wrap"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className={`pill ${info.color} flex-shrink-0`}>{info.label}</span>
                    <div className="min-w-0">
                      <div className="font-medium text-ink-800 truncate">
                        {ev.internalSkuSlug || '—'}
                      </div>
                      <div className="text-xs text-ink-500">
                        {formatDateTime(ev.createdAt)} · {ev.externalTxnId}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-ink-800 flex-shrink-0">
                    {formatCents(ev.amountCents, ev.currency)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function BillingStat({ label, value, className = '' }) {
  return (
    <div className="rounded-lg bg-white border border-gray-100 p-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-400">{label}</div>
      <div className={`text-sm font-medium text-ink-800 truncate ${className}`}>{value || '—'}</div>
    </div>
  );
}
